import React, { useState, useReducer, useContext, useEffect } from 'react'
import { cloneDeep } from 'lodash'
import { QAAddEditCategoryBatch } from 'modules/Rest'
import CardHeader from './CardHeader'
import { Typography, FormControl, TextField, MenuItem, FormGroup, FormControlLabel, Checkbox, Tooltip } from '@mui/material'
import makeStyles from '@mui/styles/makeStyles';
import { MessageContext } from './Contexts'
import { setModes } from 'modules/Utils'

const rightsDisplayValue = (value) => {
  if (value === 'null') return 'Default'
  if (value === 'false,false') return 'No rights'
  if (value === 'true,false') return 'Read questions only'
  if (value === 'true,true') return 'Read and submit questions'
}

// <select> can't handle arrays so we work with strings
const fromStringToBooleanArray = ({ value }) => {
  if (value === 'null') return null
  if (value === 'false,false') return [false, false]
  if (value === 'true,false') return [true, false]
  if (value === 'true,true') return [true, true]
}

const findIndex = (rights, type) => rights.findIndex(({ user_type }) => user_type === type)

const reducer = (rightsState, action) => {
  switch (action.type) {
    case 'rights':
      const index = findIndex(rightsState, action.payload.user_type)
      let newState = cloneDeep(rightsState)
      newState[index] = {
        ...newState[index],
        ...action.payload,
        dirty: true,
      }
      return newState

    case 'reset':
      return cloneDeep(action.payload)

    default:
      throw new Error()
  }
}

const useStyles = makeStyles(theme => ({
  row: {
    display: 'flex',
    '& > *': { flex: 1 },
  },
  controlLabel: {
    justifyContent: 'flex-end',
  },
  userTypeName: {
    marginTop: 3,
  },
}))

export default function QAUserRightsCard({ category, refreshCategory }) {
  const classes = useStyles()
  const [rightsState, dispatch] = useReducer(reducer, [])
  const [prevRightsState, setPrevRightsState] = useState([])
  const { messageDispatch } = useContext(MessageContext)

  const [mode, setMode] = useState('')

  useEffect(() => {
    dispatch({type: 'reset', payload: category.rights})
    setPrevRightsState(category.rights)
  }, [category])

  const saveCategorySettings = async (e, setMode) => {
    try {
      setMode('pending')
      let dirtyRights = rightsState.filter(({ dirty }) => dirty) // post only dirty/modified rights
      if (!category.default) {
        dirtyRights = dirtyRights.map(({ q_export, ...rest }) => rest) // filter out 'q_export' if it is not the default category
      }
      if (dirtyRights.length) {
        const { category: { rights: newRights } } = await QAAddEditCategoryBatch({rights: dirtyRights, category: category.category})
        dispatch({type: 'reset', payload: newRights})
        setPrevRightsState(newRights)
      }
      setModes(setMode, ['completing', 1500, ''])
    } catch (error) {
      setMode('')
      messageDispatch({type: 'warning', payload: error.message})
      refreshCategory()
    }
  }

  return <>
    <CardHeader
      mode={mode}
      setMode={setMode}
      action={saveCategorySettings}
      cancel={(e, setMode) => {
        dispatch({type: 'reset', payload: prevRightsState})
        setMode('')
      }}
      title='User Rights'
      headingElement='h6'
    />
    {rightsState.map(({ s_rights, e_rights, user_type, user_type_name, q_export }) => (
      <div className={classes.row} key={user_type}>
        <Typography variant='body2' className={classes.userTypeName}>{user_type_name}</Typography>
        <FormControl className={classes.formControl}>
          <Tooltip title={s_rights === null ? rightsDisplayValue(e_rights.toString()) : ''} arrow>
            <TextField
              select
              variant="standard"
              value={s_rights ? s_rights.toString() : 'null'}
              onChange={({ target }) =>
                dispatch({
                  type: 'rights',
                  payload: {
                    s_rights: fromStringToBooleanArray(target),
                    user_type,
                  },
                })
              }
              disabled={mode !== 'editing'}
            >
              <MenuItem value='null'>Default</MenuItem>
              <MenuItem value='false,false'>No rights</MenuItem>
              <MenuItem value='true,false'>Read questions only</MenuItem>
              <MenuItem value='true,true'>Read and submit questions</MenuItem>
            </TextField>
          </Tooltip>
        </FormControl>
        <FormGroup>
          <Tooltip title={!category.default ? 'Can only be set for the default category' : ''} arrow>
            <FormControlLabel
              className={classes.controlLabel}
              checked={q_export}
              onChange={() =>
                dispatch({
                  type: 'rights',
                  payload: {
                    q_export: !q_export,
                    user_type,
                  },
                })
              }
              control={<Checkbox/>}
              label='Allow Q&A export'
              disabled={mode !== 'editing' || !category.default}
            />
          </Tooltip>
        </FormGroup>
      </div>
    ))}
  </>
}
